/** @jsx jsx */
import { jsx } from "@emotion/core"

import { assert, IComponentProp } from "@cd2/tools/build/es6"
import { HoverResourceLoader } from "../hoverLoaders/HoverResourceLoader"
import { IWebClient } from "@cd2/skeem-client"
import { useEffect } from "react"
import { explodePromise, IExplodedPromise } from "../../utils/explodePromise"
import { buildRoute, eRoutes } from "../../routes"
import { buildEventEmitter } from "../../utils/EventEmitter"
import { useReloadKey } from "../../hooks/useReloadKey"
import { useGlobalState } from "../../utils/globalState"

type ICreateHoverLoaderOpts<Record, OtherProps = unknown> = {
  model: string
  attributes: unknown[]
  resourceName?: string
  Target(props: { record: Record; other: OtherProps; isTitle?: boolean }): React.ReactNode
  HoverContent(props: { record: Record; other: OtherProps }): React.ReactNode
  viewLink?:
    | eRoutes
    | ((record: Record, globalState: ReturnType<typeof useGlobalState>) => string | null)
  noHoverIcon?: boolean
}

export function createHoverLoader<Record extends { id: string }, OtherProps = unknown>(
  opts: ICreateHoverLoaderOpts<Record, OtherProps>,
) {
  HoverLoaderComponent.displayName = `HoverLoader(${opts.model})`

  class BatchedLoader {
    skeem: IWebClient | null = null
    timerRunning = false
    timer: any = null
    ids: { id: string; promise: IExplodedPromise<Record | null> }[] = []
    maxBatchSize = 40

    add(skeem: IWebClient, id: string) {
      this.skeem = skeem
      const promise = explodePromise<Record | null>()
      this.ids.push({ id, promise })
      if (this.ids.length >= this.maxBatchSize) {
        this.load()
      } else {
        this.startTimer()
      }
      return promise.promise
    }

    private startTimer() {
      if (this.timerRunning) {
        return
      }
      this.timerRunning = true
      this.timer = setTimeout(() => {
        this.load()
        this.timerRunning = false
      }, 100)
    }

    load() {
      if (this.timerRunning) {
        clearTimeout(this.timer)
        this.timerRunning = false
      }
      const ids = this.ids
      this.ids = []

      assert(this.skeem)

      this.skeem
        .fetch2<Record[]>({
          model: {
            name: opts.model,
            attributes: opts.attributes,
            matches: {
              id: { or: ids.map(i => i.id) },
            },
          },
        })
        .then(records => {
          ids.forEach(({ id, promise }) => {
            const record = records.find(r => r.id === id)
            promise.resolve(record ?? null)
          })
        })
        .catch(error => {
          ids.forEach(({ promise }) => {
            promise.reject(error)
          })
        })
    }
  }

  const batchedLoader = new BatchedLoader()

  async function loadData(skeem: IWebClient, id: string) {
    const record = await batchedLoader.add(skeem, id)

    assert(record, `Could not find ${opts.model} with id ${id}`)

    return record
  }

  const eventEmitter = buildEventEmitter()

  HoverLoaderComponent.triggerReload = triggerReload
  function triggerReload(id: string) {
    eventEmitter.trigger(id)
  }

  function HoverLoaderComponent({
    id,
    openInNewTab,
    header,
    appendToBody,
    offset,
    placement,
    inlineViewLink,
    ...otherProps
  }: {
    id: string
    openInNewTab?: boolean
    offset?: [number, number]
    header?: React.ReactNode
    appendToBody?: boolean
    placement?: IComponentProp<typeof HoverResourceLoader, "placement">
    inlineViewLink?: boolean
  } & OtherProps) {
    const globalState = useGlobalState()
    const skeem = window.skeem
    const [reloadKey, triggerReload] = useReloadKey()

    useEffect(() => {
      const off = eventEmitter.on(id, () => {
        triggerReload()
      })
      return () => off()
    }, [id])

    return (
      <HoverResourceLoader
        reloadKey={id + " " + reloadKey}
        loadData={() => loadData(skeem, id)}
        resourceName={opts.resourceName}
        openInNewTab={openInNewTab}
        offset={offset}
        header={header}
        placement={placement}
        appendToBody={appendToBody}
        noHoverIcon={opts.noHoverIcon}
        HoverContent={({ data }) => {
          // We accept React.ReactNode as a return but ts doesn't like it
          const HoverContent = opts.HoverContent as any
          return <HoverContent record={data} other={otherProps as OtherProps} />
        }}
        viewLink={
          opts.viewLink
            ? record => {
                if (typeof opts.viewLink === "function") {
                  return opts.viewLink(record, globalState)
                } else {
                  return buildRoute(opts.viewLink!, { id: record.id })
                }
              }
            : undefined
        }
        Target={({ data, isTitle }) => {
          // We accept React.ReactNode as a return but ts doesn't like it
          const Target = opts.Target as any
          return <Target record={data} other={otherProps as OtherProps} isTitle={isTitle} />
        }}
        inlineViewLink={inlineViewLink}
      />
    )
  }

  return HoverLoaderComponent
}
